import { createFileRoute, useNavigate } from "@tanstack/react-router";
import { useEffect, useState } from "react";
import { supabase } from "@/integrations/supabase/client";
import { useT } from "@/i18n";
import { toast } from "sonner";
import { Lock, Eye, EyeOff } from "lucide-react";
import logo from "@/assets/logo.png";

export const Route = createFileRoute("/reset-password")({
  component: ResetPasswordPage,
});

function ResetPasswordPage() {
  const navigate = useNavigate();
  const t = useT();
  const [ready, setReady] = useState(false);
  const [linkError, setLinkError] = useState<string | null>(null);

  // The recovery link from the email lands here with either a PKCE `?code=...`
  // query param or a legacy `#access_token=...&type=recovery` hash. We need to
  // turn that into a session before `updateUser({ password })` will work,
  // otherwise Supabase responds with "Auth session missing!".
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const url = new URL(window.location.href);
        const code = url.searchParams.get("code");
        const hash = new URLSearchParams(window.location.hash.replace(/^#/, ""));
        const accessToken = hash.get("access_token");
        const refreshToken = hash.get("refresh_token");
        const errorDesc = url.searchParams.get("error_description") || hash.get("error_description");

        if (errorDesc) {
          if (!cancelled) setLinkError(errorDesc);
          return;
        }

        if (code) {
          const { error } = await supabase.auth.exchangeCodeForSession(code);
          if (error) throw error;
          window.history.replaceState({}, "", "/reset-password");
        } else if (accessToken && refreshToken) {
          const { error } = await supabase.auth.setSession({
            access_token: accessToken,
            refresh_token: refreshToken,
          });
          if (error) throw error;
          window.history.replaceState({}, "", "/reset-password");
        } else {
          // No tokens in URL — check if we already have a session (e.g. user
          // already exchanged it on this page).
          const { data } = await supabase.auth.getSession();
          if (!data.session) {
            if (!cancelled)
              setLinkError(
                t(
                  "This reset link is invalid or has expired. Please request a new one.",
                  "Acest link de resetare este invalid sau a expirat. Te rugăm să soliciți unul nou."
                )
              );
            return;
          }
        }
        if (!cancelled) setReady(true);
      } catch (e) {
        const msg = e instanceof Error ? e.message : String(e);
        if (!cancelled) setLinkError(msg);
      }
    })();
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const [password, setPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [submitting, setSubmitting] = useState(false);


  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    if (password.length < 6) {
      toast.error(t("Password must be at least 6 characters", "Parola trebuie să aibă cel puțin 6 caractere"));
      return;
    }
    setSubmitting(true);
    const { error } = await supabase.auth.updateUser({ password });
    setSubmitting(false);
    if (error) {
      toast.error(error.message);
      return;
    }
    toast.success(t("Password updated", "Parolă actualizată"));
    navigate({ to: "/predictions" });
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-background to-accent">
      <div className="mx-auto flex min-h-screen max-w-md flex-col px-4 pt-2 pb-6">
        <div className="mb-2 text-center">
          <img src={logo} alt="" className="mx-auto h-44 w-44 object-contain drop-shadow-2xl" />
          <h1 className="text-2xl font-bold tracking-tight">{t("Set a new password", "Setează o parolă nouă")}</h1>
        </div>
        {linkError ? (
          <div className="rounded-2xl border border-destructive/40 bg-destructive/10 p-6 text-sm">
            <p className="font-semibold text-destructive">{linkError}</p>
            <button
              type="button"
              onClick={() => navigate({ to: "/login" })}
              className="mt-4 w-full rounded-lg bg-primary py-3 font-semibold text-primary-foreground hover:opacity-90"
            >
              {t("Back to sign in", "Înapoi la autentificare")}
            </button>
          </div>
        ) : !ready ? (
          <div className="rounded-2xl border bg-card p-6 text-center text-sm text-muted-foreground shadow-sm">
            {t("Verifying reset link…", "Se verifică link-ul de resetare…")}
          </div>
        ) : (
          <form onSubmit={handleSubmit} className="rounded-2xl border bg-card p-6 shadow-sm">
            <label className="block">
              <span className="flex items-center gap-1.5 text-sm font-medium"><Lock className="h-4 w-4" />{t("New password", "Parolă nouă")}</span>
              <div className="relative mt-1">
                <input type={showPassword ? "text" : "password"} autoComplete="new-password" value={password} onChange={(e) => setPassword(e.target.value)} required minLength={6} className="w-full rounded-lg border border-input bg-background px-3 py-2.5 pr-11 text-base" />
                <button
                  type="button"
                  onClick={() => setShowPassword((s) => !s)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 cursor-pointer text-muted-foreground transition-colors hover:text-foreground"
                  aria-label={showPassword ? t("Hide password", "Ascunde parola") : t("Show password", "Arată parola")}
                >
                  {showPassword ? <EyeOff className="h-5 w-5" /> : <Eye className="h-5 w-5" />}
                </button>
              </div>
            </label>
            <button type="submit" disabled={submitting} className="mt-6 w-full rounded-lg bg-primary py-3 font-semibold text-primary-foreground hover:opacity-90 disabled:opacity-50">
              {submitting ? "…" : t("Update password", "Actualizează parola")}
            </button>
          </form>
        )}

      </div>
    </div>
  );
}
